/**
 * EPIC-DM01-E: Demo Email Sent Page
 * Shows confirmation after report email is sent
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  AlertCircle, 
  ArrowLeft,
  CheckCircle2, 
  Loader2, 
  Mail,
  RefreshCw,
} from 'lucide-react';
import { useDemoMode, type DemoSession } from '@/hooks/useDemoMode';

// Rate limit: 60 seconds between resend attempts
const RESEND_COOLDOWN_MS = 60_000;

export default function DemoEmailSent() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
    getSession,
    sendEmail,
  } = useDemoMode();

  const [session, setSession] = useState<DemoSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Get masked email from URL params
  const maskedEmail = searchParams.get('email') || 'your email';

  // Load session on mount
  useEffect(() => {
    async function loadSession() {
      if (!sessionId) {
        setSessionError('No session ID provided');
        setIsLoadingSession(false);
        return;
      }

      try {
        const loadedSession = await getSession(sessionId);
        if (!loadedSession) {
          setSessionError('Session not found');
        } else {
          setSession(loadedSession);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setSessionError('Failed to load session');
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadSession();
  }, [sessionId, getSession]);

  // Calculate time until resend is allowed
  const getResendCooldownRemaining = (): number => {
    if (!lastResendTime) return 0;
    const elapsed = Date.now() - lastResendTime;
    return Math.max(0, Math.ceil((RESEND_COOLDOWN_MS - elapsed) / 1000));
  };

  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Update cooldown timer
  useEffect(() => {
    if (!lastResendTime) return;

    const interval = setInterval(() => {
      const remaining = getResendCooldownRemaining();
      setCooldownRemaining(remaining);
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastResendTime]);

  // Handle resend email
  const handleResend = async () => {
    if (!sessionId || cooldownRemaining > 0 || isResending) return;

    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      await sendEmail(sessionId);
      setResendSuccess(true);
      setLastResendTime(Date.now());
      setCooldownRemaining(RESEND_COOLDOWN_MS / 1000);
    } catch (err) {
      console.error('Failed to resend email:', err);
      setResendError(err instanceof Error ? err.message : 'Failed to resend email');
    } finally {
      setIsResending(false);
    }
  };

  // Loading state
  if (isLoadingConfig || isLoadingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!canAccessDemoMode || !isDemoModeEnabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You do not have permission to access Demo Mode.
            </p>
            <Button variant="outline" onClick={() => navigate('/home')}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Session error
  if (sessionError || !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Session Error</h2>
            <p className="text-muted-foreground mb-4">
              {sessionError || 'Unable to load session'}
            </p>
            <Button variant="outline" onClick={() => navigate('/demo')}>
              Return to Demo Landing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(`/demo/results/${sessionId}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Results
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Email Sent!</CardTitle>
            <CardDescription>
              We've sent your swing analysis report to:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email Display */}
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
                <Mail className="h-4 w-4" />
                <span className="text-sm">Sent to</span>
              </div>
              <p className="font-medium text-lg">{maskedEmail}</p>
            </div>

            {/* Success/Error Messages */}
            {resendSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md p-4 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <p>Email resent successfully!</p>
              </div>
            )}

            {resendError && (
              <div className="bg-destructive/10 text-destructive rounded-md p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Failed to resend</p>
                  <p className="text-sm text-destructive/80">{resendError}</p>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Check your inbox (and spam folder) for an email with your detailed PDF report and activation link.
              </p>
              <p>
                The email includes a secure link to activate your account and access your full analysis history.
              </p>
            </div>

            {/* Resend Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResend}
              disabled={isResending || cooldownRemaining > 0}
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : cooldownRemaining > 0 ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend in {cooldownRemaining}s
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend Email
                </>
              )}
            </Button>

            {/* View Results Link */}
            <Button
              variant="link"
              className="w-full"
              onClick={() => navigate(`/demo/results/${sessionId}`)}
            >
              View Results on Screen
            </Button>
          </CardContent>
        </Card>

        {/* New Demo Button */}
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/demo')}
          >
            Start New Demo
          </Button>
        </div>
      </main>
    </div>
  );
}
